import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAddSignatureMutation,
  useGetApplicationByIdQuery,
} from 'src/app/services/claim/claimApi';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { ArrowBack, InfoOutlined } from '@mui/icons-material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SimpleDialog from '@fuse/core/SimpleDialog';
import { useThemeMediaQuery } from '@fuse/hooks';
import { QRCode } from 'react-qrcode-logo';
import { Tooltip } from '@mui/material';
import AssignersDrawer from './components/AssignersDrawer';
import BarCode from 'app/shared-components/BarCode/BarCode';
import handleError from '@fuse/core/errorHandler';
import useMessage from 'src/app/hooks/useMessage';
import { useSafeNavigateBack } from '@fuse/hooks/useNavigateBack';
import FileDrawer from './components/FileDrawer';
import PdfRenderWithPath from 'app/shared-components/PdfRenderWithPath/PdfRenderWithPath';

const ViewApplication = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useSafeNavigateBack('/apps/documents/my-applications');
  const { t } = useTranslation('translations');

  const [open, setOpen] = useState(false);
  const [isQRCodeVisible, setIsQRCodeVisible] = useState(false);
  const [assigners, setAssigners] = useState(null);
  const [name, setName] = useState(null);
  const [singleFile, setSingleFile] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  const { data } = useGetApplicationByIdQuery({ id }, { skip: !id });
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('md'));
  const [addSignature, { isLoading: isSigning }] = useAddSignatureMutation();
  const { showMessage } = useMessage();

  const newOne = data?.statusId === 1;
  const isAssigned = data?.statusId === 3;
  const isRejected = data?.statusId === 4;

  const color = isAssigned
    ? 'text-green-700'
    : isRejected
    ? 'text-red-700'
    : newOne
    ? 'text-orange-600'
    : 'text-blue-500';

  const handleSignDocument = async () => {
    if (!id) return;
    try {
      await addSignature({ id: +id, comment: '' }).unwrap();
      showMessage({ message: t('DocumentSuccessfullySigned'), variant: 'success' });
      navigate('/apps/documents-2/outgoing-document');
    } catch (error) {
      handleError(error);
    }
  };

  const handleOpenModal = () => {
    handleSignDocument();
  };

  const handleOpenPdfDialog = () => {
    setPdfDialogOpen(true);
  };

  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
  };

  return (
    <div className="min-h-full text-black bg-gray-100 dark:bg-gray-900 dark:text-white">
      <div className="flex items-center justify-between gap-10 p-10 bg-white shadow-md dark:bg-dark">
        <button onClick={goBack} className="text-blue">
          <ArrowBack className="mb-2 mr-2 text-16" />
          {t('ToList')}
        </button>
        <div>
          {data?.isNeedSignature && data?.code && (
            <div>
              <span className="font-bold text-red text-14 animate-pulse">
                {t('NeedToSignDocument')}
              </span>
              <button
                disabled={isSigning}
                onClick={handleOpenModal}
                className="px-20 py-8 ml-20 text-white bg-green rounded-8 text-bold hover:bg-green-600"
              >
                {t('ToSign')}
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-12">
          <div className="flex gap-2">
            <span>{t('AttachedFiles')}</span>
            <Tooltip
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
              title={t('Files')}
              placement="bottom"
            >
              <AttachFileIcon className="ml-6 text-blue-800 cursor-pointer" />
            </Tooltip>
          </div>
          <div className="flex gap-2">
            <span>{t('Status')}:</span> <span className={color}>{data?.status}</span>
            <Tooltip
              onClick={(e) => {
                e.stopPropagation();
                setAssigners(data.approvers);
                setName(data.name);
              }}
              title={t('Assignments')}
              placement="bottom"
            >
              <InfoOutlined className="ml-6 text-blue-800 cursor-pointer" />
            </Tooltip>
          </div>
        </div>
      </div>
      <div className="flex justify-center w-full mt-5">
        {data && (
          <div className="shadow-md min-w-[96rem] w-fit min-h-screen bg-white dark:bg-black">
            <div className="flex justify-center mb-4">
              <BarCode id={id} />
            </div>
            <div className="mx-48 my-40 break-words">
              {parse(data.content.replaceAll('\\n', '<br />'))}
            </div>
          </div>
        )}
      </div>

      <SimpleDialog
        title={t('SignDocument')}
        open={isQRCodeVisible}
        onClose={() => setIsQRCodeVisible(false)}
      >
        <div className="flex flex-col items-center">
          <QRCode
            value={data?.code}
            ecLevel="M"
            size={isMobile ? 192 : 256}
            quietZone={10}
            logoWidth={50}
            logoHeight={50}
            logoPadding={1}
            qrStyle="squares"
            style={{ borderRadius: 10 }}
          />
          <p>{t('ScanQRCodeWithUportalApplication')}</p>
        </div>
      </SimpleDialog>
      <AssignersDrawer assigners={assigners} setAssigners={setAssigners} name={name} data={data} />
      <FileDrawer
        setPdfDialogOpen={setPdfDialogOpen}
        open={open}
        onClose={() => setOpen(false)}
        data={data}
        setSingleFile={setSingleFile}
        onOpenPdfDialog={handleOpenPdfDialog}
      />
      <PdfRenderWithPath open={pdfDialogOpen} onClose={handleClosePdfDialog} path={singleFile} />
    </div>
  );
};

export default ViewApplication;
